import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  DoCheck,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  viewChild,
  signal,
  WritableSignal,
  inject
} from '@angular/core';
import { CoreUtilsService } from '@bepatient/core';
import { IonDatetime, IonItem, IonModal } from '@ionic/angular';
import chroma from 'chroma-js';
import { format, parseISO, isValid, parse, isAfter, isBefore } from 'date-fns';
import { Subscription } from 'rxjs';
import { DatetimeQuestionV3 } from '../../../classes/question-datetime';
import { QuestionBaseV3ComponentDirective } from '../question-base.component';

@Component({
  selector: 'bepatient-input-datetime-v3',
  templateUrl: './input-datetime.component.html',
  styleUrls: ['./input-datetime.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class InputDatetimeComponentV3
  extends QuestionBaseV3ComponentDirective
  implements AfterViewInit, OnDestroy, DoCheck, OnInit
{
  private utils = inject(CoreUtilsService);
  private cdr = inject(ChangeDetectorRef);

  @Input() question: DatetimeQuestionV3;
  @Input() color: string;

  /**
   * Indicates whether the datetime component is ready
   * @TODO Remove isDateTimeReady usage from HTML & Class when the bug (https://github.com/ionic-team/ionic-framework/issues/30706) is fixed
   */
  public isDateTimeReady: WritableSignal<boolean> = signal(false);

  isBlur = true;
  hasError: boolean;
  subscription: Subscription;
  statusSubscription: Subscription;
  parsedDateTimeValue = '';
  fieldErrors: Array<string>;
  finalValue: string = '';
  dirty: boolean;
  pickerCurrentDate: string = undefined;
  userLocale: string;

  @ViewChild(IonItem, { read: ElementRef, static: true })
  private ionItem: ElementRef;

  private ionDateTime = viewChild(IonDatetime);

  @ViewChild(IonModal)
  private modal: IonModal;

  get value() {
    return this.formGroup.get(this.question.key).value;
  }

  displayValue: string = '';

  constructor() {
    super();
    this.userLocale = localStorage.getItem('locale') || '';
  }

  ngAfterViewInit(): void {
    this.ionItem.nativeElement.style.setProperty(
      '--input-valid-background',
      chroma(this.color).alpha(0.1).css()
    );

    // Initialize the picker with current value
    this.initializePickerValue();
    this.parsedDateTimeValue = this.formGroup.get(this.question.key).value;
  }

  private initializePickerValue(): void {
    const ctrl = this.formGroup.get(this.question.key);
    const currentValue = ctrl?.value;

    if (currentValue) {
      // Keep the current control value; just sync picker to it
      const parsed =
        this.parseDateValue(currentValue) || new Date(currentValue);
      if (parsed && !isNaN(parsed.getTime())) {
        this.pickerCurrentDate = this.getPickerFormatForDate(parsed);
      }
      return;
    }

    // No current value → seed picker with now for all types, but DO NOT patch the control
    // const now = new Date();
    this.pickerCurrentDate = undefined; // By default value should be empty or else keep using the default value.
    // Note: even for 12h datetime, we do not set the control here. The value
    // will only be committed in onConfirm()/onClear().
  }

  private getPickerFormatForDate(date: Date): string {
    switch (this.question.type) {
      case 'date':
        return format(date, 'yyyy-MM-dd');
      case 'datetime':
      default:
        return format(date, "yyyy-MM-dd'T'HH:mm:ss");
    }
  }

  ngOnInit(): void {
    // Initialize display values
    this.updateDisplayValues();

    // Subscribe to form control changes
    if (this.question && this.formGroup) {
      const formControl = this.formGroup.get(this.question.key);

      // Subscribe to value changes
      this.subscription = formControl.valueChanges.subscribe(() => {
        this.updateDisplayValues();
        this.dirty = formControl.dirty;
        this.cdr.markForCheck();
      });

      // Subscribe to status changes for error detection
      this.statusSubscription = formControl.statusChanges.subscribe(() => {
        this.dirty = formControl.dirty;
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Action - DateTime Modal Did Present
   * Called when the datetime modal is fully presented
   * @TODO Remove this when the bug  (https://github.com/ionic-team/ionic-framework/issues/30706) is fixed
   */
  public onDateTimeModalDidPresent(): void {
    requestAnimationFrame(() => {
      this.isDateTimeReady.set(true);
    });
  }

  /**
   * Action - DateTime Modal Will Dismiss
   * Called when the datetime modal is about to close
   * @TODO Remove this when the bug  (https://github.com/ionic-team/ionic-framework/issues/30706) is fixed
   */
  public onDateTimeModalWillDismiss(): void {
    this.isDateTimeReady.set(false);
  }

  private updateDisplayValues(): void {
    const rawValue = this.formGroup.get(this.question.key).value;
    this.displayValue = this.getDisplayValue(rawValue);
    this.finalValue = this.displayValue;
  }

  ngDoCheck(): void {
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  getFormattedDate(value) {
    if (!value) {
      this.initializePickerValue();
      return;
    }

    // Handle date and datetime fields
    // Parse the value using date-fns with multiple formats
    const parsedDate = this.parseDateValue(value);

    if (parsedDate && isValid(parsedDate)) {
      // Set the picker date for the modal based on field type and format
      this.pickerCurrentDate = this.getPickerFormatForDate(parsedDate);

      // Store the value using the question's format
      const dateFnsFormat = this.convertToDateFnsFormat(this.question.format);
      let backendValue: string;

      try {
        backendValue = format(parsedDate, dateFnsFormat);

        // Additional validation to prevent NaN values
        if (backendValue.includes('NaN') || backendValue.includes('Invalid')) {
          this.formGroup.controls[this.question.key].patchValue('');
          this.finalValue = '';
          this.displayValue = '';
          this.cdr.markForCheck();
          return;
        }
      } catch (error) {
        // Invalid date - clear the form control to prevent NaN values
        this.formGroup.controls[this.question.key].patchValue('');
        this.finalValue = '';
        this.displayValue = '';
        this.cdr.markForCheck();
        return;
      }

      // Update the form control with the backend-compatible value

      this.formGroup.controls[this.question.key].patchValue(backendValue);

      // Update the display value
      this.finalValue = this.getDisplayValue(backendValue);
      this.displayValue = this.finalValue;
      this.cdr.markForCheck();
    } else {
      // Fallback: try to parse as Date
      const date = new Date(value);
      if (!isNaN(date.getTime()) && isValid(date)) {
        // Set picker value based on field type and format
        this.pickerCurrentDate = this.getPickerFormatForDate(date);

        const dateFnsFormat = this.convertToDateFnsFormat(this.question.format);
        let backendValue: string;

        try {
          backendValue = format(date, dateFnsFormat);

          // Additional validation to prevent NaN values
          if (
            backendValue.includes('NaN') ||
            backendValue.includes('Invalid')
          ) {
            this.formGroup.controls[this.question.key].patchValue('');
            this.finalValue = '';
            this.displayValue = '';
            this.cdr.markForCheck();
            return;
          }
        } catch (error) {
          // Invalid date - clear the form control to prevent NaN values
          this.formGroup.controls[this.question.key].patchValue('');
          this.finalValue = '';
          this.displayValue = '';
          this.cdr.markForCheck();
          return;
        }

        this.formGroup.controls[this.question.key].patchValue(backendValue);
        this.finalValue = this.getDisplayValue(backendValue);
        this.displayValue = this.finalValue;
        this.cdr.markForCheck();
      } else {
        // Both parsing attempts failed - clear the form control to prevent NaN values
        this.formGroup.controls[this.question.key].patchValue('');
        this.finalValue = '';
        this.displayValue = '';
        this.cdr.markForCheck();
      }
    }
  }

  private parseDateValue(value: string): Date | null {
    // Try different parsing formats, starting with the question's format
    const questionFormat = this.convertToDateFnsFormat(this.question.format);
    const formats = [
      questionFormat, // Primary format from question
      'dd/MM/yyyy HH:mm', // Common datetime format
      'dd/MM/yyyy h:mm a', // 12-hour datetime format
      'dd/MM/yyyy', // Common date format
      'HH:mm', // Common time format
      'h:mm a', // 12-hour time format
      "yyyy-MM-dd'T'HH:mm:ss", // ISO format (fallback)
      "yyyy-MM-dd'T'h:mm a", // 12-hour ISO datetime format
      "yyyy-MM-dd'T'HH:mm:ss.SSSX",
      "yyyy-MM-dd'T'HH:mm:ssX",
      'MM/dd/yyyy HH:mm',
      'MM/dd/yyyy h:mm a', // 12-hour datetime format (US)
      'yyyy-MM-dd HH:mm',
      'yyyy-MM-dd h:mm a', // 12-hour datetime format (ISO style)
      'MM/dd/yyyy',
      'yyyy-MM-dd',
      'HH:mm:ss'
    ];

    // Use a fixed reference date to avoid timezone issues
    const referenceDate = new Date(2025, 0, 1, 12, 0, 0); // Jan 1, 2025, 12:00:00

    for (const formatStr of formats) {
      try {
        const parsed = parse(value, formatStr, referenceDate);
        if (isValid(parsed)) {
          return parsed;
        }
      } catch (error) {
        // Continue to next format
      }
    }

    // Try parseISO as last resort
    try {
      const isoParsed = parseISO(value);
      if (isValid(isoParsed)) {
        return isoParsed;
      }
    } catch (error) {
      // Return null if all parsing fails
    }

    return null;
  }

  private isValidDateRange(dateString: string): boolean {
    const parsedDate = this.parseDateValue(dateString);
    if (!parsedDate || !isValid(parsedDate)) {
      return false;
    }

    const dateConstraints = this.getDateConstraints();

    if (dateConstraints.min) {
      const minDate = this.parseDateValue(dateConstraints.min);
      if (minDate && isBefore(parsedDate, minDate)) {
        return false;
      }
    }

    if (dateConstraints.max) {
      const maxDate = this.parseDateValue(dateConstraints.max);
      if (maxDate && isAfter(parsedDate, maxDate)) {
        return false;
      }
    }

    return true;
  }

  private getDateConstraints(): { min?: string; max?: string } {
    const constraints = this.question.constraints || [];
    const dateConstraints: { min?: string; max?: string } = {};

    constraints.forEach(constraint => {
      if (constraint.min !== undefined) {
        dateConstraints.min = constraint.min.toString();
      }
      if (constraint.max !== undefined) {
        dateConstraints.max = constraint.max.toString();
      }
    });

    return dateConstraints;
  }

  onDateSelected(dateString) {
    if (dateString) {
      // Parse the selected date/time value using date-fns
      const parsedDate = this.parseDateValue(dateString);

      if (parsedDate && isValid(parsedDate)) {
        // Validate date constraints
        const dateStringForValidation = format(parsedDate, 'yyyy-MM-dd');
        if (!this.isValidDateRange(dateStringForValidation)) {
          this.formGroup.controls[this.question.key].setErrors({
            invalidDateRange: true
          });
          return;
        }

        // Clear any previous errors
        this.formGroup.controls[this.question.key].setErrors(null);

        // Set the picker date for the modal
        this.pickerCurrentDate = this.getPickerFormatForDate(parsedDate);

        // Store the value using the question's format
        const dateFnsFormat = this.convertToDateFnsFormat(this.question.format);
        let backendValue: string;

        try {
          backendValue = format(parsedDate, dateFnsFormat);

          // Additional validation to prevent NaN values
          if (
            backendValue.includes('NaN') ||
            backendValue.includes('Invalid')
          ) {
            this.formGroup.controls[this.question.key].patchValue('');
            this.finalValue = '';
            this.displayValue = '';
            this.cdr.markForCheck();
            return;
          }
        } catch (error) {
          // Invalid date - clear the form control to prevent NaN values
          this.formGroup.controls[this.question.key].patchValue('');
          this.finalValue = '';
          this.displayValue = '';
          this.cdr.markForCheck();
          return;
        }

        // Update the form control with the backend-compatible value
        this.formGroup.controls[this.question.key].patchValue(backendValue);

        // Update the display value
        this.finalValue = this.getDisplayValue(backendValue);
        this.displayValue = this.finalValue;
        this.cdr.markForCheck();
      } else {
        // Fallback: try to parse using the old method
        let { userFormat, isoFormat } = this.utils.getValidDateFormat(
          this.question.mobiDateFormat.toLowerCase(),
          dateString
        );

        this.pickerCurrentDate = isoFormat ? isoFormat : void 0;

        // Store the raw value in form control and display formatted value
        this.formGroup.controls[this.question.key].patchValue(userFormat);
        this.finalValue = this.getDisplayValue(userFormat);
        this.displayValue = this.finalValue;
      }
    } else {
      // When date picker is cleared, reset the value to undefined
      this.pickerCurrentDate = void 0;
      this.formGroup.controls[this.question.key].setValue('');
      this.finalValue = '';
      this.displayValue = '';
    }
  }

  onBlur(): void {
    this.isBlur = true;
    const formControl = this.formGroup.get(this.question.key);
    formControl.markAsDirty();
    formControl.markAsTouched();
    this.dirty = formControl.dirty;
    this.cdr.markForCheck();
  }

  onFocus(): void {
    this.isBlur = false;
    const formControl = this.formGroup.get(this.question.key);
    this.dirty = formControl.dirty;
    this.initializePickerValue();
    this.cdr.markForCheck();
  }

  onChange(value): void {
    this.isBlur = false;
    const formControl = this.formGroup.get(this.question.key);
    this.dirty = formControl.dirty;
    formControl.markAsDirty();
    this.onDateSelected(value);
    this.cdr.markForCheck();
  }

  isEmpty(): boolean {
    const v = this.formGroup?.get(this.question.key)?.value;
    return v === undefined || v === null || v === '';
  }

  onConfirm(value: string): void {
    this.dirty = this.formGroup.get(this.question.key).dirty;
    this.onDateSelected(value);
    const ctrl = this.formGroup.get(this.question.key);
    ctrl.markAsDirty();
    ctrl.markAsTouched();
    this.isBlur = true;
  }

  onCancel(): void {
    const ctrl = this.formGroup.get(this.question.key);
    this.isBlur = true;
    ctrl.markAsPristine();
    ctrl.markAsUntouched();
  }

  onClear(): void {
    const ctrl = this.formGroup.get(this.question.key);
    this.pickerCurrentDate = void 0;
    ctrl.setValue('');
    ctrl.markAsDirty();
    ctrl.markAsTouched();
    this.isBlur = true;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }

  get presentation(): string {
    switch (this.question.type) {
      case 'date':
        return 'date';
      case 'datetime':
      default:
        return 'date-time';
    }
  }

  get hourCycle(): string {
    if (this.question.format.includes('hh')) {
      return 'h12';
    }
    return 'h23';
  }

  async onInputClick(): Promise<void> {
    // This method is called when the input is clicked
    this.onFocus();

    // Manually open the modal since trigger attribute might not work
    if (this.modal) {
      await this.modal.present();
    }
  }

  private getDisplayValue(value: any): string {
    if (!value) return '';

    // Handle different field types
    switch (this.question.type) {
      case 'date':
        return this.formatDateDisplay(value);
      case 'datetime':
      default:
        return this.formatDateTimeDisplay(value);
    }
  }

  private formatDateDisplay(value: string): string {
    // Format date using the question's format
    if (!value) return '';

    const parsedDate = this.parseDateValue(value);

    if (parsedDate && isValid(parsedDate)) {
      const dateFnsFormat = this.convertToDateFnsFormat(
        this.question.format || 'dd/mm/yyyy'
      );
      return format(parsedDate, dateFnsFormat);
    }

    return value;
  }

  private formatDateTimeDisplay(value: string): string {
    // Format datetime using the question's format
    if (!value) return '';

    const parsedDate = this.parseDateValue(value);

    if (parsedDate && isValid(parsedDate)) {
      const dateFnsFormat = this.convertToDateFnsFormat(
        this.question.format || 'dd/mm/yyyy HH:mm'
      );
      return format(parsedDate, dateFnsFormat);
    }

    return value;
  }

  private convertToDateFnsFormat(format: string): string {
    // Defensive defaults
    if (!format) return 'dd/MM/yyyy HH:mm';

    let result = format;

    // Normalize case for common tokens first
    // (we accept either upper/lower from backend but date-fns expects these)
    result = result.replace(/YYYY/g, 'yyyy');
    result = result.replace(/DD/g, 'dd');

    // Hours: keep both 24h (HH) and 12h (hh) as-is
    // Minutes/Months disambiguation around separators
    // Slash-separated
    result = result.replace(/dd\/mm/g, 'dd/MM'); // dd/mm -> dd/MM
    result = result.replace(/mm\/dd/g, 'MM/dd'); // mm/dd -> MM/dd
    result = result.replace(/mm\/yyyy/g, 'MM/yyyy'); // mm/yyyy -> MM/yyyy
    result = result.replace(/yyyy\/mm/g, 'yyyy/MM'); // yyyy/mm -> yyyy/MM

    // Dash-separated
    result = result.replace(/dd-mm/g, 'dd-MM'); // dd-mm -> dd-MM
    result = result.replace(/mm-dd/g, 'MM-dd'); // mm-dd -> MM-dd
    result = result.replace(/mm-yyyy/g, 'MM-yyyy'); // mm-yyyy -> MM-yyyy
    result = result.replace(/yyyy-mm/g, 'yyyy-MM'); // yyyy-mm -> yyyy-MM

    // Datetime with T (slashes or dashes). Keep T literal.
    // If backend sends lowercase 't', normalize it to literal 'T'
    result = result.replace(/t/g, 'T');

    // Ensure AM/PM token for date-fns is lowercase 'a'
    result = result.replace(/A/g, 'a');

    // *** IMPORTANT: Force meridiem when 12-hour token is present ***
    // If the format contains 'hh' but no 'a', append ' a' immediately after the minutes/seconds block.
    // Handle common variants: hh:mm, hh:mm:ss, and with 'T' in between date/time.
    if (/hh/.test(result) && !/\ba\b/i.test(result)) {
      // hh:mm[:ss]
      result = result.replace(
        /hh:(mm)(:ss)?(?!\s*a)/i,
        (m, mm, ss) => `hh:${mm}${ss || ''} a`
      );
      // Also cover cases where there's a space before minutes (rare): hh mm
      result = result.replace(/hh\s*:\s*mm(?!\s*a)/i, 'hh:mm a');
    }

    return result;
  }
}
